/**
 * SaleDialog — Phase 18
 * --------------------------------------------------------------------------
 * Lets the user sell loose raw material from a packet to any free-text
 * party. Generates an SM/… memo.
 *
 * CRITICAL UX requirement: sale must NOT alter the packet's running-average
 * rate. The UI surface explicitly communicates this.
 *
 * Works for diamonds / color_stones / pearls — pass `material` prop.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { formatCarats, formatINR } from '@/lib/format';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateCaratMovement } from '@/lib/caratValidation';
import { ShoppingCart, AlertCircle, TrendingUp } from 'lucide-react';

const MATERIAL_META = {
  diamond:     { base: '/diamonds',     label: 'Diamond',      testidScope: 'diamond' },
  color_stone: { base: '/color-stones', label: 'Colour Stone', testidScope: 'color-stone' },
  pearl:       { base: '/pearls',       label: 'Pearl',        testidScope: 'pearl' },
};

export default function SaleDialog({
  open,
  onOpenChange,
  material,
  packet,
  onSold,
}) {
  const meta = MATERIAL_META[material];
  const scope = meta?.testidScope || 'pkt';

  const [partyName, setPartyName] = useState('');
  const [partyContact, setPartyContact] = useState('');
  const [carats, setCarats] = useState('');
  const [pieces, setPieces] = useState('');
  const [saleRate, setSaleRate] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPartyName('');
    setPartyContact('');
    setCarats('');
    setPieces('');
    setSaleRate('');
    setReference('');
    setNote('');
  }, [open]);

  const ct = Number(carats) || 0;
  const pcs = Number(pieces) || 0;
  const costRate = Number(packet?.current_rate_per_ct || 0);
  const srNum = Number(saleRate) > 0 ? Number(saleRate) : costRate;
  const costValue = ct * costRate;
  const saleValue = ct * srNum;
  const margin = saleValue - costValue;

  const exceedsBalance = packet
    && (ct - Number(packet.balance_carats || 0) > 1e-9
        || pcs > Number(packet.balance_pieces || 0));

  const submit = async () => {
    if (!packet) return;
    if (!partyName.trim()) {
      toast.error('Party name is required');
      return;
    }
    // Wave 3.49 Rule 1 + Rule 2 — both qty > 0; source balance check;
    // source-side post-move state must remain consistent.
    const ruleErr = validateCaratMovement({
      txnCarats: ct, txnPieces: pcs,
      availableCarats: packet.balance_carats || 0,
      availablePieces: packet.balance_pieces || 0,
      packetNo: packet.packet_no,
      context: 'sale of raw material',
    });
    if (ruleErr) { toast.error(ruleErr); return; }
    setSubmitting(true);
    try {
      const payload = {
        carats: ct,
        pieces: pcs,
        party_name: partyName.trim(),
        party_contact: partyContact.trim() || undefined,
        reference: reference || '',
        note: note || '',
      };
      if (Number(saleRate) > 0) payload.sale_rate_per_ct = Number(saleRate);
      const { data } = await api.post(`${meta.base}/packets/${packet.id}/sale`, payload);
      toast.success(`Sale ${data.memo_no} recorded`);
      onOpenChange(false);
      if (onSold) onSold(data);
    } catch (e) {
      toast.error(extractError(e, 'Sale failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!meta || !packet) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`sale-dialog-${scope}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart size={16} className="text-emerald-700" />
            Sale of Raw Material — {meta.label}
          </DialogTitle>
          <DialogDescription>
            Sell loose material from this packet to any party. A sale memo (SM/…) will be generated.
            Packet&apos;s running average / fixed rate will <strong>not</strong> change.
          </DialogDescription>
        </DialogHeader>

        <Card className="p-3 bg-white mt-1">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>Packet</span>
            <span className="font-mono text-[12px] text-slate-700" data-testid="sale-packet-no">{packet.packet_no}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-2">
            <div>
              <div className="text-[10px] uppercase text-slate-400">Available</div>
              <div className="font-mono tabular-nums text-sm" data-testid="sale-available-balance">
                {formatCarats(packet.balance_carats || 0)} ct · {packet.balance_pieces || 0} pcs
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-400">Cost rate (₹/ct)</div>
              <div className="font-mono tabular-nums text-sm">
                {costRate.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-400">Mode</div>
              <div className="text-sm">
                <Badge variant="secondary" className="text-[10px]">
                  {packet.valuation_mode === 'fixed' ? 'Fixed' : 'Run. Avg'}
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs" htmlFor="sale-party">Party name *</Label>
              <Input
                id="sale-party"
                value={partyName}
                onChange={(e) => setPartyName(e.target.value)}
                placeholder="e.g. ABC Jewellers"
                data-testid="sale-party-name-input"
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor="sale-contact">Party contact — optional</Label>
              <Input
                id="sale-contact"
                value={partyContact}
                onChange={(e) => setPartyContact(e.target.value)}
                placeholder="Phone, email, GSTIN, address…"
                data-testid="sale-party-contact-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs" htmlFor="sale-carats">Carats *</Label>
              <Input
                id="sale-carats"
                type="number"
                step="0.01"
                min="0"
                value={carats}
                onChange={(e) => setCarats(e.target.value)}
                placeholder="0.00"
                data-testid="sale-carats-input"
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor="sale-pieces">Pieces *</Label>
              <Input
                id="sale-pieces"
                type="number"
                min="0"
                value={pieces}
                onChange={(e) => setPieces(e.target.value)}
                placeholder="0"
                data-testid="sale-pieces-input"
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor="sale-rate">Sale rate (₹/ct)</Label>
              <Input
                id="sale-rate"
                type="number"
                step="0.01"
                min="0"
                value={saleRate}
                onChange={(e) => setSaleRate(e.target.value)}
                placeholder={`Default ${costRate.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                data-testid="sale-rate-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs" htmlFor="sale-reference">Reference — optional</Label>
              <Input
                id="sale-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Invoice / bill no."
                data-testid="sale-reference-input"
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor="sale-note">Note — optional</Label>
              <Input
                id="sale-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Additional details"
                data-testid="sale-note-input"
              />
            </div>
          </div>

          <Card className="p-3 bg-slate-50 border-slate-200">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-slate-400">Cost value</div>
                <div className="font-mono tabular-nums" data-testid="sale-cost-value">
                  {formatINR(costValue)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-400">Sale value</div>
                <div className="font-mono tabular-nums" data-testid="sale-sale-value">
                  {formatINR(saleValue)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-400 flex items-center gap-1">
                  <TrendingUp size={11} /> Margin
                </div>
                <div
                  className={`font-mono tabular-nums ${margin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                  data-testid="sale-margin-value"
                >
                  {formatINR(margin)}
                </div>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              Stock balance reduces at the packet&apos;s cost rate. Packet rate is preserved.
            </div>
          </Card>

          {exceedsBalance && (
            <div
              className="flex items-start gap-2 text-rose-700 text-[12px] bg-rose-50 border border-rose-200 rounded px-2 py-1.5"
              data-testid="sale-exceeds-warning"
            >
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <div>
                Carats or pieces exceed packet&apos;s available balance
                {ct - Number(packet.balance_carats || 0) > 1e-9 && <> ({ct} ct &gt; {packet.balance_carats} ct)</>}
                {pcs > Number(packet.balance_pieces || 0) && <> ({pcs} pcs &gt; {packet.balance_pieces} pcs)</>}
                . Reduce the quantity to continue.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="sale-cancel"
          >
            Cancel
          </Button>
          <Button
            className="luxury-button"
            onClick={submit}
            disabled={
              submitting
              || !partyName.trim()
              || (ct <= 0 && pcs <= 0)
              || exceedsBalance
            }
            data-testid="sale-submit"
          >
            {submitting ? 'Posting…' : 'Confirm Sale'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
